import React, { useState, useCallback, forwardRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';
import type { LogOutAction } from '@helpCenter/epics/logout';
import { isAdvancedHelpCenter } from '@helpCenter/util/advanced-help-center';
import { ThemedBadge as AvatarBadge } from '@helpCenter/view/common/badges';
import { ForgeActionModule } from '@helpCenter/view/forge-ui/forge-action';
import { isWrmOptimizationsEnabled, isUnifiedHelpReportingPageEnabled } from 'feature-flags';
import { useIntl } from 'react-intl-next';
import type { Column } from 'rest/requests-column-settings';
import * as grid from 'view/styles/grid';
import { sizes } from 'view/styles/sizes-viewport';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { Profile } from '@atlaskit/atlassian-navigation';
import Avatar from '@atlaskit/avatar';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import HomeIcon from '@atlaskit/icon/glyph/home';
import { ButtonItem, CustomItem, MenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import type { PopupComponentProps, TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { colors, layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { Link } from '@atlassian/help-center-common-component/analytics';
import { initializeConnect } from '@atlassian/help-center-common-component/connect-fragment';
import { HELP_CENTERS_MANAGEMENT_BASE_PATH } from '@atlassian/help-center-common-component/constants';
import {
    USER_MENU_ACTION_COMPONENT,
    USER_MENU_ACTION_SECTION_MODULE,
} from '@atlassian/help-center-common-component/forge-ui/constants';
import { ForgeModalContext } from '@atlassian/help-center-common-component/forge-ui/modal-context';
import { ForgeModalContextProvider } from '@atlassian/help-center-common-component/forge-ui/modal-context-provider';
import type { SetExtensionToDisplayType } from '@atlassian/help-center-common-component/forge-ui/types';
import { InteractiveElement } from '@atlassian/help-center-common-component/interactive-element';
import { getRelativeBasePath } from '@atlassian/help-center-common-util/history';
import { withDeferredWrmLoaded } from '@atlassian/help-center-common-util/wrm';
import MultiHcDropdown from '../multi-hc-dropdown';
import messages from './messages';

export interface CustomMenuLinkProps {
    id: string | undefined;
    key: string | undefined;
    label: string | undefined;
    href: string;
    styleClass: string | undefined;
}

interface Portal {
    id?: number;
    name?: string;
    isProjectSimplified: boolean;
    isProjectAdmin: boolean;
    canEditAnnouncement: boolean;
}
export interface StateProps {
    avatarSrc: string | undefined;
    customMenuLinks: CustomMenuLinkProps[];
    displayName?: string;
    routeName?: string;
    portal?: Portal;
    pendingApprovalCount?: number;
    approvalCount?: number;
    canAdministerJIRA: boolean;
    canCustomiseHelpCenter?: boolean;
    myRequestCount?: number;
    defaultColumnSettings?: Column[];
    jsmAIConfigEnabled?: boolean;
}

export interface DispatchProps {
    onLogoutClick: () => LogOutAction;
    onOpenAndClose?: (e: { isOpen: boolean }) => void;
    openHelpCenterCustomizationSidebar: () => void;
    openRequestListCustomizationSidebar: () => void;
}

export type Props = StateProps & DispatchProps;

const homePath = '/servicedesk/customer/portals';
const basePath = isAdvancedHelpCenter() ? '/' : '/portals';
const isHelpCenterManagementScreen = getRelativeBasePath() === HELP_CENTERS_MANAGEMENT_BASE_PATH;

interface ForgeTitleProps {
    onClose: () => void;
}
export const ForgeTitleMenuLinks = ({ onClose }: ForgeTitleProps) => {
    const {
        data: { extensionTitles },
        methods: { setExtensionToDisplay: onClick },
    } = React.useContext(ForgeModalContext);

    const forgeActionLinkOnClick = (id: string, onClickApp: SetExtensionToDisplayType) => () => {
        onClose();
        return onClickApp(id);
    };

    return (
        <>
            {extensionTitles.map(({ id, title }) => {
                return (
                    <CustomItem id={id} key={id} component={CustomButton} onClick={forgeActionLinkOnClick(id, onClick)}>
                        {title}
                    </CustomItem>
                );
            })}
        </>
    );
};

const logoutLink = (onLogoutClick: () => LogOutAction) => (
    <CustomBottomPadding>
        <Section hasSeparator>
            <AnalyticsContext data={{ actionSubjectId: 'navbarLogout' }}>
                <CustomItem component={CustomButton} aria-label={'logout'} onClick={onLogoutClick}>
                    <FormattedMessage {...messages.logout} />
                </CustomItem>
            </AnalyticsContext>
        </Section>
    </CustomBottomPadding>
);

const reportingMenuLink = () => {
    return (
        <CustomItem actionSubjectId="navbarReporting" component={CustomLink} to="/reporting">
            <FormattedMessage {...messages.reporting} />
        </CustomItem>
    );
};

export const CustomMenuLinks = ({
    customMenuLinks,
    onClose,
}: {
    customMenuLinks: CustomMenuLinkProps[];
    onClose: () => void;
}) => (
    <span data-test-id="custom-menu-link">
        {customMenuLinks.map((menuLink) => (
            <div key={menuLink.key} className={menuLink.styleClass}>
                <CustomItem
                    actionSubjectId="custom-menu-link"
                    component={CustomLink}
                    to={menuLink.href}
                    onClick={onClose}
                    data-test-id="custom-menu-link"
                    key={menuLink.id}
                >
                    {menuLink.label}
                </CustomItem>
            </div>
        ))}
    </span>
);

const buildCustomMenuLinks = (customMenuLinks: CustomMenuLinkProps[], onClose: () => void) => {
    return customMenuLinks.map((menuLink) => (
        <div key={menuLink.key} className={menuLink.styleClass}>
            <CustomItem
                actionSubjectId="custom-menu-link"
                component={CustomLink}
                data-test-id="custom-menu-link"
                key={menuLink.id}
                onClick={onClose}
                to={menuLink.href}
            >
                {menuLink.label}
            </CustomItem>
        </div>
    ));
};

const TopMenu = (myRequestCount: number, onClose: () => void, pendingApprovalCount: number, approvalCount: number) => {
    const customizeMenu = [
        {
            onClick: onClose,
            link: isHelpCenterManagementScreen ? '/servicedesk/customer/user/requests' : '/user/requests',
            label: <FormattedMessage {...messages.requests} />,
            actionSubjectId: 'navbarUserRequests',
            iconAfter: myRequestCount > 0 ? <AvatarBadge>{myRequestCount}</AvatarBadge> : undefined,
            isVisible: true,
        },
        {
            onClick: onClose,
            link: isHelpCenterManagementScreen ? '/servicedesk/customer/user/approvals' : '/user/approvals',
            label: <FormattedMessage {...messages.approvals} />,
            actionSubjectId: 'navbarUserApprovals',
            iconAfter: pendingApprovalCount > 0 ? <AvatarBadge>{pendingApprovalCount}</AvatarBadge> : undefined,
            isVisible: approvalCount > 0 || pendingApprovalCount > 0,
        },
        {
            onClick: onClose,
            link: isHelpCenterManagementScreen ? '/servicedesk/customer/user/profile' : '/user/profile',
            label: <FormattedMessage {...messages.profile} />,
            actionSubjectId: 'navbarProfile',
            isVisible: true,
        },
    ];
    return (
        <Section>
            {customizeMenu.map((menu) => {
                if (!menu.isVisible) {
                    return null;
                }
                return (
                    <>
                        {isHelpCenterManagementScreen ? (
                            <CustomItem
                                key={menu.actionSubjectId}
                                component={CustomAnchor}
                                href={menu.link}
                                onClick={menu.onClick}
                                iconAfter={menu.iconAfter}
                            >
                                {menu.label}
                            </CustomItem>
                        ) : (
                            <CustomItem
                                key={menu.actionSubjectId}
                                component={CustomLink}
                                to={menu.link}
                                actionSubjectId={menu.actionSubjectId}
                                onClick={menu.onClick}
                                iconAfter={menu.iconAfter}
                            >
                                {menu.label}
                            </CustomItem>
                        )}
                    </>
                );
            })}
        </Section>
    );
};

const renderMultiHcDropdown = () => {
    return (
        <Section hasSeparator>
            <SectionHeading>
                <FormattedMessage {...messages.helpCenter} />
            </SectionHeading>
            <Box xcss={MultiHcDropdownContainer}>
                <MultiHcDropdown />
            </Box>
        </Section>
    );
};

const CustomizeLinks = (
    onClose: () => void,
    openHelpCenterCustomizationSidebar: DispatchProps['openHelpCenterCustomizationSidebar'],
    canAdministerJIRA: boolean,
    routeName: string | undefined
) => {
    const isHomePage = routeName === 'help-center';
    const isSinglePortalPage = routeName === 'portal-home';
    const isSmallScreen = window.innerWidth < sizes.xsmall;
    const customizeMenu = [
        {
            link: `${basePath}?customize=true&experience=login-announcement`,
            label: <FormattedMessage {...messages.signIn} />,
            actionSubjectId: 'navbarLoginAnnouncement',
            isVisible: isHomePage && canAdministerJIRA && !isSmallScreen,
        },
        {
            link: `${basePath}?customize=true&experience=help-center-announcement`,
            label: <FormattedMessage {...messages.announcement} />,
            actionSubjectId: 'navbarAnnouncement',
            isVisible: isHomePage && !isSmallScreen,
        },
        {
            link: `${basePath}?customize=true&experience=theme`,
            label: <FormattedMessage {...messages.look} />,
            actionSubjectId: 'navbarLookAndFeel',
            isVisible: (isHomePage || isSinglePortalPage) && canAdministerJIRA,
        },
        {
            link: '/settings/portal-reorder',
            label: <FormattedMessage {...messages.topic} />,
            actionSubjectId: 'navbarPortalReorder',
            isVisible: isHomePage && canAdministerJIRA && !isSmallScreen,
        },
    ];
    const onClick = () => {
        onClose();
        openHelpCenterCustomizationSidebar();
    };
    return (
        <Section>
            {customizeMenu.map((menu) => {
                if (!menu.isVisible) {
                    return null;
                }
                return (
                    <CustomItem
                        key={menu.actionSubjectId}
                        component={CustomLink}
                        to={menu.link}
                        actionSubjectId={menu.actionSubjectId}
                        onClick={onClick}
                    >
                        {menu.label}
                    </CustomItem>
                );
            })}
        </Section>
    );
};

const profile = (displayName: string, onClose: () => void) => {
    const onHomeIconClick = () => {
        if (isHelpCenterManagementScreen) {
            window.location.href = homePath;
        }
        onClose();
    };
    return (
        <>
            <IconWrapper>
                <NoUnderlineThemeLink
                    to={isHelpCenterManagementScreen ? undefined : basePath}
                    actionSubjectId="home"
                    onClick={onHomeIconClick}
                >
                    <HomeIcon label="" aria-hidden="true" size="medium" />
                </NoUnderlineThemeLink>

                <InteractiveElement tagName="span" onClick={onClose} aria-label="close">
                    <CrossIcon label="" aria-hidden="true" size="medium" />
                </InteractiveElement>
            </IconWrapper>
            <StyledUserName as="h2" level="h700">
                {displayName}
            </StyledUserName>
        </>
    );
};

const portalLinks = (onClose: () => void, canCustomiseHelpCenter: boolean | undefined, portal: Portal | undefined) => {
    if (!portal) {
        return null;
    }
    const { isProjectSimplified, isProjectAdmin, canEditAnnouncement } = portal;
    const shouldRenderAsPortalAnnouncement = (): boolean => {
        // for simplified projects, render side bar as announcement for agents with permission
        if (isProjectSimplified && !isProjectAdmin && canEditAnnouncement) {
            return true;
        }
        // in classic projects render sidebar as announcement for users who can't modify helpcenter, but can edit announcements
        if (!isProjectSimplified && !canCustomiseHelpCenter && canEditAnnouncement) {
            return true;
        }
        return false;
    };
    const customizeMenu = [
        {
            link: '?customize=true&experience=add-announcement',
            onClick: onClose,
            label: <FormattedMessage {...messages.portalAnnouncement} />,
            actionSubjectId: 'navbarPortalAnnouncement',
            isVisible: canEditAnnouncement || shouldRenderAsPortalAnnouncement(),
        },
        {
            link: '?customize=true&experience=request-groups',
            onClick: onClose,
            label: <FormattedMessage {...messages.portalRequestTypeGroup} />,
            actionSubjectId: 'navbarRequestTypeGroup',
            isVisible: isProjectAdmin && isProjectSimplified && !shouldRenderAsPortalAnnouncement(),
        },
    ];
    const showHeading = customizeMenu.some((menu) => menu.isVisible);
    return (
        <Section>
            {showHeading && (
                <SectionHeading>
                    <FormattedMessage {...messages.portal} />
                </SectionHeading>
            )}
            {customizeMenu.map((menu) => {
                if (!menu.isVisible) {
                    return null;
                }
                return (
                    <CustomItem
                        key={menu.actionSubjectId}
                        component={CustomLink}
                        to={menu.link}
                        actionSubjectId={menu.actionSubjectId}
                        onClick={onClose}
                    >
                        {menu.label}
                    </CustomItem>
                );
            })}
        </Section>
    );
};

const RequestList = (
    onClose: () => void,
    openRequestListCustomizationSidebar: DispatchProps['openRequestListCustomizationSidebar']
) => {
    const onClick = () => {
        onClose();
        openRequestListCustomizationSidebar();
    };
    return (
        <Section>
            <SectionHeading>
                <FormattedMessage {...messages.requestListHeading} />
            </SectionHeading>
            <CustomItem
                component={CustomLink}
                to="/user/requests"
                actionSubjectId="navbarUserRequestListCustomize"
                onClick={onClick}
            >
                <FormattedMessage {...messages.customizeRequestList} />
            </CustomItem>
        </Section>
    );
};

const UserProfileConnectActionLinks = withDeferredWrmLoaded(CustomMenuLinks, initializeConnect);

const AvatarMenuDumbV2 = (props: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const { formatMessage } = useIntl();
    const onItemClick = () => {
        setIsOpen(false);
    };

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (!!props.onOpenAndClose) {
                props.onOpenAndClose({ isOpen: open });
            }
            setIsOpen(open);
        },
        [props]
    );

    const content = () => {
        const {
            displayName,
            myRequestCount,
            customMenuLinks,
            onLogoutClick,
            openHelpCenterCustomizationSidebar,
            openRequestListCustomizationSidebar,
            routeName,
            pendingApprovalCount,
            approvalCount,
            canAdministerJIRA,
            canCustomiseHelpCenter,
            defaultColumnSettings,
            portal,
            jsmAIConfigEnabled,
        } = props;
        const isSmallScreen = window.innerWidth < sizes.xsmall;
        return (
            <Wrapper>
                {displayName && profile(displayName, onItemClick)}
                <MenuGroup maxHeight={maxHeight} spacing={'cozy'}>
                    {TopMenu(myRequestCount || 0, onItemClick, pendingApprovalCount || 0, approvalCount || 0)}
                    {!isHelpCenterManagementScreen && (
                        <>
                            {!isSmallScreen && canAdministerJIRA && renderMultiHcDropdown()}
                            {canCustomiseHelpCenter &&
                                CustomizeLinks(
                                    onItemClick,
                                    openHelpCenterCustomizationSidebar,
                                    canAdministerJIRA,
                                    routeName
                                )}
                            {routeName === 'portal-home' && portalLinks(onItemClick, canCustomiseHelpCenter, portal)}
                            {routeName === 'requests-list' &&
                                canAdministerJIRA &&
                                Boolean(defaultColumnSettings?.length) &&
                                RequestList(onItemClick, openRequestListCustomizationSidebar)}
                            {isUnifiedHelpReportingPageEnabled() && jsmAIConfigEnabled && canAdministerJIRA
                                ? reportingMenuLink()
                                : null}
                            {customMenuLinks && customMenuLinks.length > 0 ? (
                                <Section hasSeparator={customMenuLinks && customMenuLinks.length > 0}>
                                    {isWrmOptimizationsEnabled() ? (
                                        <UserProfileConnectActionLinks
                                            customMenuLinks={customMenuLinks}
                                            onClose={onItemClick}
                                        />
                                    ) : (
                                        buildCustomMenuLinks(customMenuLinks, onItemClick)
                                    )}
                                </Section>
                            ) : undefined}
                            <ForgeTitleMenuLinks onClose={onItemClick} />
                        </>
                    )}
                    {logoutLink(onLogoutClick)}
                </MenuGroup>
            </Wrapper>
        );
    };

    const Trigger = useCallback(
        (triggerProps: Partial<TriggerProps>) => {
            const { avatarSrc, pendingApprovalCount = 0, myRequestCount = 0 } = props;
            const totalRequests = pendingApprovalCount + myRequestCount;
            return (
                <>
                    {totalRequests > 0 && <StyledAvatarBadge>{totalRequests}</StyledAvatarBadge>}
                    <div role="list">
                        <Profile
                            {...triggerProps}
                            icon={<Avatar src={avatarSrc} borderColor={token('color.border.inverse', colors.N0)} />}
                            isSelected={isOpen}
                            onClick={() => onOpenChange(!isOpen)}
                            aria-label={formatMessage(messages.tooltip)}
                            tooltip={formatMessage(messages.tooltip)}
                        />
                    </div>
                </>
            );
        },
        [formatMessage, isOpen, onOpenChange, props]
    );

    const CustomPopupContainer = useMemo(
        () =>
            // eslint-disable-next-line react/display-name
            forwardRef<HTMLDivElement, PopupComponentProps>((popupProps, ref) => (
                <PopupCustomContainer {...popupProps} ref={ref} />
            )),
        []
    );
    const renderDropdownMenu = () => {
        return (
            <Popup
                // This is required so that we can close the dropdown when the user clicks outside of it even if the click target has moved out of the DOM due to cases like route change or drawer close
                shouldUseCaptureOnOutsideClick
                isOpen={isOpen}
                popupComponent={CustomPopupContainer}
                onClose={() => onOpenChange(false)}
                placement="bottom-end"
                zIndex={layers.modal()}
                content={content}
                trigger={Trigger}
            />
        );
    };

    return (
        <AvatarMenuContainer>
            <ForgeModalContextProvider type={USER_MENU_ACTION_SECTION_MODULE}>
                {renderDropdownMenu()}
                <ForgeActionModule type={USER_MENU_ACTION_SECTION_MODULE} componentName={USER_MENU_ACTION_COMPONENT} />
            </ForgeModalContextProvider>
        </AvatarMenuContainer>
    );
};

AvatarMenuDumbV2.defaultProps = {
    customMenuLinks: [],
};

const MENU_WIDTH = grid.multiple(39).px;
const AvatarMenuContainer = styled.div({
    paddingRight: token('space.100', '8px'),
    position: 'relative',
});

const NoUnderlineLink = styled(Link)({
    textDecoration: 'none',
    '&:focus, &:hover, &:visited, &:link, &:active': {
        textDecoration: 'none',
    },
});
const PopupCustomContainer = styled.div({
    backgroundColor: token('elevation.surface.overlay', '#fff'),
    boxShadow: token(
        'elevation.shadow.overlay',
        '0px 4px 8px rgba(9, 30, 66, 0.25), 0px 0px 1px rgba(9, 30, 66, 0.31)'
    ),
    borderRadius: token('space.150', '12px'),
    maxWidth: MENU_WIDTH,
    borderColor: token('color.border', colors.N30),
    '&:focus': {
        outline: 'none',
    },
});

const Wrapper = styled.div({
    width: MENU_WIDTH,
});

const IconWrapper = styled.div({
    display: 'flex',
    padding: `${token('space.150', '12px')} ${token('space.200', '16px')} ${token('space.100', '8px')}${token(
        'space.200',
        '16px'
    )}`,
    justifyContent: 'space-between',
    color: token('color.icon', '#44546F'),
});

const StyledUserName = styled(Heading)({
    margin: 'auto',
    width: '80%',
    textAlign: 'center',
    paddingBottom: token('space.150', '12px'),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const StyledAvatarBadge = styled(AvatarBadge)({
    position: 'absolute',
    left: 0,
    bottom: 0,
    zIndex: layers.navigation(),
});

const SectionHeading = styled.div({
    paddingLeft: token('space.400', '32px'),
    marginTop: token('space.200', '16px'),
    fontSize: '12px',
    textTransform: 'uppercase',
    fontWeight: 600,
});

const NoUnderlineThemeLink = styled(NoUnderlineLink)({
    color: 'unset !important',
});

const CustomBottomPadding = styled.div({
    paddingBottom: token('space.100', '8px'),
});

const CustomLink = styled(NoUnderlineThemeLink)({
    fontWeight: 500,
    padding: `${token('space.150', '12px')} ${token('space.300', '24px')} ${token('space.150', '12px')} ${token(
        'space.400',
        '32px'
    )}`,
});

const CustomButton = styled(ButtonItem)({
    fontWeight: 500,
    padding: `${token('space.150', '12px')} ${token('space.300', '24px')} ${token('space.150', '12px')} ${token(
        'space.400',
        '32px'
    )}`,
});

const CustomAnchor = styled.a({
    fontWeight: 500,
    padding: `${token('space.150', '12px')} ${token('space.300', '24px')} ${token('space.150', '12px')} ${token(
        'space.400',
        '32px'
    )}`,
    color: 'unset !important',
    textDecoration: 'none',
    '&:focus, &:hover, &:visited, &:link, &:active': {
        textDecoration: 'none',
    },
});

const maxHeight = `calc(100vh - ${grid.multiple(24).px}`;

const MultiHcDropdownContainer = xcss({
    paddingBottom: 'space.100',
    paddingLeft: 'space.400',
    paddingRight: 'space.400',
    paddingTop: 'space.100',
});

export default AvatarMenuDumbV2;
